import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, CheckCircle2, AlertTriangle, X, Copy, RefreshCw, ExternalLink, KeyRound } from 'lucide-react';
import { useToastStore } from '../../stores/toast';
import {
  getClaudeAuthStatus, startClaudeActivation, getClaudeActivationStatus,
  completeClaudeActivation, claudeLogout, type ClaudeAuthStatus,
} from '../../api/admin';

type Phase = 'idle' | 'starting' | 'awaiting_paste' | 'submitting' | 'polling' | 'done';

/**
 * Modal that activates/reactivates the Claude agent (subscription
 * OAuth) through the agentic-bridge, surfaced in the admin UI so a non-shell
 * operator can bind the instance to a Claude account.
 *
 * Empirical flow: `claude auth login --claudeai` prints an OAuth URL (remote
 * redirect to platform.claude.com -- works on a remote server) then prompts
 * the operator to paste the code returned in the browser. The modal shows
 * the status, a clickable link, a copyable code area, a paste field, and
 * polls activation status every 2s while pending. ASCII only; Tailwind.
 */
export default function ClaudeActivationModal({ onClose }: { onClose: () => void }) {
  const { addToast } = useToastStore();
  const [status, setStatus] = useState<ClaudeAuthStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [phase, setPhase] = useState<Phase>('idle');
  const [handle, setHandle] = useState<string | null>(null);
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [flow, setFlow] = useState<string>('device_code');
  const [pasteCode, setPasteCode] = useState('');
  const [pollMsg, setPollMsg] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mounted = useRef(true);

  const refreshStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const s = await getClaudeAuthStatus();
      if (!mounted.current) return;
      setStatus(s);
    } catch {
      if (mounted.current) addToast('Failed to read AI agent auth status', 'error');
    } finally {
      if (mounted.current) setLoadingStatus(false);
    }
  }, [addToast]);

  useEffect(() => {
    mounted.current = true;
    refreshStatus();
    return () => {
      mounted.current = false;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [refreshStatus]);

  const stopPolling = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const pollStatus = useCallback((h: string) => {
    stopPolling();
    const tick = async () => {
      try {
        const r = await getClaudeActivationStatus(h);
        if (!mounted.current) return;
        setPollMsg(r.message || null);
        if (r.status === 'complete') {
          stopPolling();
          setPhase('done');
          addToast('AI agent activated', 'success');
          await refreshStatus();
          return;
        }
        if (r.status === 'error') {
          stopPolling();
          setPhase('awaiting_paste');
          addToast(r.message || 'Activation failed', 'error');
          return;
        }
        // pending -> keep polling every 2s.
        pollTimer.current = setTimeout(tick, 2000);
      } catch {
        if (mounted.current) {
          // transient; retry once more after 2s.
          pollTimer.current = setTimeout(tick, 2000);
        }
      }
    };
    pollTimer.current = setTimeout(tick, 2000);
  }, [addToast, refreshStatus]);

  const handleStart = async () => {
    setPhase('starting');
    setPollMsg(null);
    try {
      const r = await startClaudeActivation();
      if (!mounted.current) return;
      setHandle(r.handle);
      setAuthUrl(r.auth_url);
      setFlow(r.flow || 'device_code');
      if (r.auth_url) {
        setPhase('awaiting_paste');
        pollStatus(r.handle);
      } else {
        // No URL captured (bridge could not drive the CLI non-interactively);
        // still let the operator attempt a paste.
        setPhase('awaiting_paste');
        setPollMsg(r.auth_url === null ? 'No OAuth URL was captured. You can still paste a code if you obtained one manually.' : null);
        pollStatus(r.handle);
      }
    } catch (e) {
      if (mounted.current) {
        setPhase('idle');
        addToast('Failed to start activation', 'error');
      }
    }
  };

  const handleSubmit = async () => {
    if (!handle || !pasteCode.trim()) {
      addToast('Paste the activation code first', 'error');
      return;
    }
    stopPolling();
    setPhase('submitting');
    try {
      const r = await completeClaudeActivation(handle, pasteCode.trim());
      if (!mounted.current) return;
      setPollMsg(r.message || null);
      if (r.status === 'complete') {
        setPhase('done');
        addToast('AI agent activated', 'success');
        await refreshStatus();
      } else if (r.status === 'pending') {
        setPhase('polling');
        pollStatus(handle);
      } else {
        setPhase('awaiting_paste');
        addToast(r.message || 'Activation failed', 'error');
      }
    } catch (e) {
      if (mounted.current) {
        setPhase('awaiting_paste');
        addToast('Activation complete failed', 'error');
      }
    }
  };

  const copyCode = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      addToast('Copied', 'success');
    } catch {
      addToast('Copy failed', 'error');
    }
  };

  const busy = phase === 'starting' || phase === 'submitting' || phase === 'polling';
  const [deactivating, setDeactivating] = useState(false);

  const handleDeactivate = async () => {
    if (!window.confirm('Deactivate the current AI agent account? The active session will end.')) return;
    setDeactivating(true);
    try {
      const r = await claudeLogout();
      if (!mounted.current) return;
      addToast(r.ok ? 'AI agent account deactivated' : (r.message || 'Logout failed'), r.ok ? 'success' : 'error');
      await refreshStatus();
    } catch (e) {
      if (mounted.current) addToast('Logout failed', 'error');
    } finally {
      if (mounted.current) setDeactivating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div className="card p-6 w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <KeyRound className="text-primary-600" size={20} />
            <h2 className="text-lg font-semibold text-slate-800">Activate / Reactivate AI Agent</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Auth status row */}
        <div className="flex items-center justify-between gap-4 py-3 border-t border-slate-100">
          <div className="flex items-start gap-2">
            {loadingStatus ? (
              <Loader2 className="text-slate-400 animate-spin mt-0.5" size={18} />
            ) : status?.authenticated ? (
              <CheckCircle2 className="text-green-600 mt-0.5" size={18} />
            ) : (
              <AlertTriangle className="text-amber-600 mt-0.5" size={18} />
            )}
            <div>
              <p className="text-sm font-medium text-slate-800">
                {loadingStatus ? 'Checking status...' : status?.authenticated ? 'Authenticated' : 'Not authenticated'}
              </p>
              {status?.authenticated && (
                <p className="text-xs text-slate-600 mt-0.5">
                  {status?.account
                    ? `Authenticated as ${status.account}` +
                      (status.org_name ? ` (${status.org_name}` +
                        (status.subscription_type ? `, ${status.subscription_type}` : '') + ')' : '')
                    : 'Authenticated (account unknown)'}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-0.5">
                {status?.auth_method ? `Method: ${status.auth_method}` : 'No active session'}
                {status?.token_expires_at
                  ? ` - expires ${new Date(status.token_expires_at).toLocaleString()}`
                  : ''}
                {status?.creds_file_present === false ? ' - creds file missing' : ''}
              </p>
              {status?.message && (
                <p className="text-xs text-amber-700 mt-0.5">{status.message}</p>
              )}
            </div>
          </div>
          <button
            onClick={refreshStatus}
            disabled={loadingStatus}
            className="px-2.5 py-1.5 text-xs font-medium text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Deactivate / Switch account (only when authenticated) */}
        {status?.authenticated && phase === 'idle' && (
          <div className="py-3 border-t border-slate-100 flex items-center justify-between gap-4">
            <p className="text-xs text-slate-500">
              Deactivate the current account to switch to a different one. The
              active session will end.
            </p>
            <button
              onClick={handleDeactivate}
              disabled={deactivating || loadingStatus}
              className="px-3 py-1.5 text-xs font-medium text-red-700 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50 flex items-center gap-1"
            >
              {deactivating ? <Loader2 className="animate-spin" size={14} /> : <KeyRound size={14} />}
              Deactivate / Switch account
            </button>
          </div>
        )}

        {/* Start activation */}
        {phase === 'idle' && (
          <div className="py-4">
            <p className="text-xs text-slate-500 mb-3">
              Start the activation to get a link that binds this instance to your
              AI agent account. You will approve in the browser, then paste the
              code shown there back here.
            </p>
            <button
              onClick={handleStart}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Start Activation
            </button>
          </div>
        )}

        {/* Starting spinner */}
        {phase === 'starting' && (
          <div className="py-6 flex items-center gap-2 text-sm text-slate-600">
            <Loader2 className="animate-spin" size={18} /> Starting activation...
          </div>
        )}

        {/* Awaiting paste / polling */}
        {(phase === 'awaiting_paste' || phase === 'polling' || phase === 'submitting') && (
          <div className="py-4 space-y-4">
            {authUrl && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Step 1 - Open this link and approve
                </label>
                <div className="flex items-center gap-2">
                  <a
                    href={authUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-700 underline break-all inline-flex items-center gap-1"
                  >
                    <ExternalLink size={14} /> {authUrl.length > 60 ? authUrl.slice(0, 57) + '...' : authUrl}
                  </a>
                  <button
                    onClick={() => copyCode(authUrl)}
                    className="text-slate-400 hover:text-slate-600 shrink-0"
                    aria-label="Copy link"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Flow: {flow}. After approving, the page displays a code - paste it below.
                </p>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Step 2 - Paste activation code
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={pasteCode}
                  onChange={(e) => setPasteCode(e.target.value)}
                  placeholder="Paste the code from the approval page"
                  className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-400 focus:border-primary-400 outline-none"
                />
                <button
                  onClick={handleSubmit}
                  disabled={busy || !pasteCode.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {phase === 'submitting' ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>

            {phase === 'polling' && (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="animate-spin" size={16} /> Waiting for approval...
              </div>
            )}
            {pollMsg && phase !== 'polling' && (
              <p className="text-xs text-amber-700">{pollMsg}</p>
            )}
          </div>
        )}

        {/* Done */}
        {phase === 'done' && (
          <div className="py-4 flex items-center gap-2 text-sm text-green-700">
            <CheckCircle2 size={18} /> AI agent activated. You can close this window.
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2 pt-4 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}